import {
  Modal,
  Panel,
  PanelType,
  ProgressIndicator,
  Stack
} from "@fluentui/react";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import { DisplayProxyDocumentInfo } from "./components/DisplayProxyDocumentInfo";

import { ApprovalStep } from "@/components/Contracts/contract.interface";
import { ViewProgress } from "@/components/Contracts/ViewProgress";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { useConfirmModal } from "@/hooks/useConfirmModal";
import useQueryParams from "@/hooks/useQueryParams";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import TermLogs from "@/modules/Terms/components/TermLogs";
import { TermSignaturesModal } from "@/modules/Terms/components/TermSignaturesModal";
import { TermViewMenu } from "@/modules/Terms/components/ViewMenu/TermsViewMenu";
import {
  ITerm,
  TermTypeEnum
} from "@/modules/Terms/interfaces/ITerm.interface";
import { handleTermRouteRedirection } from "@/modules/Terms/utils/handleTermRouteRedirection";

export const ViewProxyDocument = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [term, setTerm] = useState<ITerm>();
  const [steps, setSteps] = useState<ApprovalStep[]>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showSignaturesModal, setShowSignaturesModal] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const { selectedBU } = useSelectedBu();
  const { execute } = useApi();
  const { queryParams, changeQueryParams } = useQueryParams();
  const { openModalWithSettings } = useConfirmModal();
  const { id: termId } = useParams<{ id: string }>();

  const displayLogs = queryParams.occurrences === "true";

  const handleDisplayTermLogs = useCallback(
    (isOpen: boolean) => {
      changeQueryParams({ occurrences: isOpen ? "true" : undefined });
    },
    [changeQueryParams]
  );

  const getTerm = useCallback(async () => {
    setIsLoading(true);

    const response = await execute<ITerm>({
      url: `terms/${termId}`,
      notificationMessage: `Buscando procuração #${termId}`,
      params: {
        businessActionId: selectedBU?.id
      }
    });

    handleTermRouteRedirection({
      response,
      termId,
      navigate,
      expectedTermType: TermTypeEnum.ASSOCIATION_AND_PROXY,
      openModalWithSettings
    });

    const data = response.data;

    setCurrentStep(data?.currentStep?.order);
    setSteps(data?.approvalFlow?.steps);
    setTerm(data);
    setIsLoading(false);
  }, [navigate, execute, selectedBU?.id, openModalWithSettings, termId]);

  useEffect(() => {
    if (selectedBU) {
      setIsLoading(false);
    }
  }, [selectedBU]);

  useEffect(() => {
    void getTerm();
  }, [getTerm]);

  return (
    <>
      <Helmet>
        <title>Visualização de procuração - Libertas</title>
      </Helmet>

      {isLoading && (
        <ProgressIndicator
          styles={{
            root: { position: "absolute", left: 0, right: 0, top: 0 },
            itemProgress: { padding: 0 }
          }}
        />
      )}

      {term && (
        <Stack tokens={{ childrenGap: 15 }} style={{ position: "relative" }}>
          <TermViewMenu
            term={term}
            refetchTerm={getTerm}
            currentStep={term?.currentStep}
            setIsLoading={setIsLoading}
            handleDisplayTermLogs={handleDisplayTermLogs}
          />

          <div>
            {steps?.length > 0 && (
              <ViewProgress currentStep={currentStep} steps={steps} />
            )}
          </div>

          <DisplayProxyDocumentInfo term={term} refetchTerm={getTerm} />

          <Modal
            isOpen={showSignaturesModal}
            onDismiss={() => setShowSignaturesModal(false)}
            isBlocking={false}
          >
            <TermSignaturesModal
              term={term}
              toggleVisible={setShowSignaturesModal}
            />
          </Modal>

          <Panel
            headerText="Histórico"
            isOpen={displayLogs}
            onDismiss={() => handleDisplayTermLogs(false)}
            type={PanelType.medium}
            closeButtonAriaLabel="Fechar"
            isLightDismiss
          >
            <TermLogs termId={term.id} />
          </Panel>
        </Stack>
      )}
    </>
  );
};
